import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { KeyRound, Loader2, Mail, Phone, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useSeo } from "@/hooks/useSeo";
import { useHref, useT } from "@/i18n";

export default function ClaimListing() {
  const navigate = useNavigate();
  const href = useHref();
  const t = useT();
  const [params] = useSearchParams();
  const pakej = params.get("pakej") || "";
  const [phone, setPhone] = useState("");
  const [matches, setMatches] = useState(null);
  const [selected, setSelected] = useState(null);
  const [email, setEmail] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("phone");
  const [busy, setBusy] = useState(false);

  useSeo({
    title: t("claim.seo.title"),
    description: t("claim.seo.desc"),
    canonical: `${window.location.origin}${window.location.pathname}`,
  });

  const lookup = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/claims/lookup", { phone });
      setMatches(data.matches);
      if (data.matches.length === 0) toast.error(t("claim.toast.none"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const start = async (match, withEmail) => {
    setBusy(true);
    setSelected(match);
    try {
      const { data } = await api.post("/claims/start", {
        listing_id: match.listing_id,
        email: withEmail || undefined,
      });
      if (data.mode === "admin_review") {
        setStep("admin_review");
        toast.success(t("claim.toast.sent"));
      } else {
        setMaskedEmail(data.masked_email);
        setStep("code");
        toast.success(t("claim.toast.codeSent", { email: data.masked_email }));
      }
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      if (detail.includes("tiada emel")) {
        setStep("need_email");
      } else {
        toast.error(detail || err.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/claims/verify", { listing_id: selected.listing_id, code });
      localStorage.setItem(`dirbiz_claim_${data.listing_id}`, data.claim_token);
      toast.success(t("claim.toast.verified"));
      navigate(href(`/manage/${data.listing_id}`), { state: { justClaimed: true } });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-14 sm:px-8">
      <span className="grid h-11 w-11 place-items-center rounded-md bg-slate-900 text-white">
        <ShieldCheck className="h-5 w-5" />
      </span>
      <h1 className="mt-6 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
        {t("claim.title")}
      </h1>
      <p className="mt-3 max-w-xl text-base leading-relaxed text-slate-600">
        {t("claim.intro")}
      </p>

      {pakej && (
        <div
          data-testid="claim-package-note"
          className="mt-6 rounded-lg border border-amber-300 bg-amber-50 p-5"
        >
          <p className="font-display text-base font-bold tracking-tight text-amber-950">
            {t("claim.pkg.title", { pakej })}
          </p>
          <p className="mt-1.5 text-sm leading-relaxed text-amber-900">
            {t("claim.pkg.body")}
          </p>
        </div>
      )}

      <div
        data-testid="claim-explainer"
        className="mt-6 grid gap-4 rounded-lg border border-border bg-slate-50 p-5 sm:grid-cols-2"
      >
        <div>
          <p className="text-sm font-semibold text-slate-900">{t("claim.have.title")}</p>
          <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
            {t("claim.have.body")}
          </p>
        </div>
        <div className="border-t border-border pt-4 sm:border-l sm:border-t-0 sm:pl-5 sm:pt-0">
          <p className="text-sm font-semibold text-slate-900">{t("claim.none.title")}</p>
          <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
            {t("claim.none.body")}
          </p>
          <Link to={href("/submit")} className="mt-3 inline-block">
            <Button size="sm" data-testid="claim-register-instead-button" className="bg-primary hover:bg-[#0052CC]">
              {t("claim.none.cta")}
            </Button>
          </Link>
        </div>
      </div>

      {step === "phone" && (
        <form onSubmit={lookup} className="mt-10 rounded-lg border border-border bg-card p-6 shadow-sm">
          <Label htmlFor="claim-phone">{t("claim.phone")}</Label>
          <div className="mt-2 flex flex-col gap-3 sm:flex-row">
            <Input
              id="claim-phone"
              data-testid="claim-phone-input"
              placeholder="012-345 6789"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="h-11"
            />
            <Button
              type="submit"
              data-testid="claim-lookup-button"
              disabled={busy || !phone}
              className="h-11 bg-primary hover:bg-[#0052CC]"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Phone className="mr-2 h-4 w-4" />}
              {t("claim.lookup")}
            </Button>
          </div>

          {matches && matches.length > 0 && (
            <div className="mt-8 space-y-3" data-testid="claim-matches">
              {matches.map((m) => (
                <div
                  key={m.listing_id}
                  data-testid={`claim-match-${m.listing_id}`}
                  className="flex flex-col gap-3 rounded-lg border border-border p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div>
                    <p className="font-display font-semibold tracking-tight">{m.business_name}</p>
                    <p className="mt-1 text-sm text-slate-500">
                      {m.district}, {m.state}
                    </p>
                    <Badge variant="outline" className="mt-2 text-[11px]">
                      {m.has_email ? t("claim.codeTo", { email: m.masked_email }) : t("claim.adminNeeded")}
                    </Badge>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    data-testid={`claim-select-${m.listing_id}`}
                    disabled={busy}
                    onClick={() => start(m, null)}
                  >
                    {t("claim.select")}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </form>
      )}

      {step === "need_email" && (
        <div className="mt-10 rounded-lg border border-border bg-card p-6 shadow-sm" data-testid="claim-need-email">
          <p className="text-sm leading-relaxed text-slate-700">
            {t("claim.needEmail.body", { name: selected?.business_name })}
          </p>
          <Label htmlFor="claim-email" className="mt-6 block">
            {t("claim.email")}
          </Label>
          <div className="mt-2 flex flex-col gap-3 sm:flex-row">
            <Input
              id="claim-email"
              type="email"
              data-testid="claim-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-11"
            />
            <Button
              data-testid="claim-request-review-button"
              disabled={busy || !email}
              onClick={() => start(selected, email)}
              className="h-11 bg-primary hover:bg-[#0052CC]"
            >
              <Mail className="mr-2 h-4 w-4" /> {t("claim.requestReview")}
            </Button>
          </div>
        </div>
      )}

      {step === "admin_review" && (
        <div
          data-testid="claim-admin-review"
          className="mt-10 rounded-lg border border-amber-200 bg-amber-50 p-6 text-sm leading-relaxed text-amber-900"
        >
          {t("claim.adminReview.body", { name: selected?.business_name })}
        </div>
      )}

      {step === "code" && (
        <form onSubmit={verify} className="mt-10 rounded-lg border border-border bg-card p-6 shadow-sm">
          <p className="text-sm text-slate-600">
            {t("claim.code.sent", { email: maskedEmail })}
          </p>
          <Label htmlFor="claim-code" className="mt-6 block">
            {t("claim.code.label")}
          </Label>
          <div className="mt-2 flex flex-col gap-3 sm:flex-row">
            <Input
              id="claim-code"
              data-testid="claim-code-input"
              inputMode="numeric"
              maxLength={6}
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="h-11 tracking-[0.4em]"
            />
            <Button
              type="submit"
              data-testid="claim-verify-button"
              disabled={busy || code.length < 6}
              className="h-11 bg-primary hover:bg-[#0052CC]"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}
              {t("claim.verify")}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
